/**
 * ImageRotatorDialog Component
 *
 * A dialog component for displaying an image with adjustable rotation.
 * Users can modify the image's rotation angle using a slider, reset it to the default position, or close the dialog.
 *
 * @component
 * @param {ImageRotatorDialogProps} props - The component's props.
 * 
 * @typedef {Object} ImageRotatorDialogProps
 * @property {string} imageUrl - The URL of the image to display. Defaults to '/placeholder.svg?height=300&width=300'.
 * @property {() => void} onClose - Callback function to handle closing the dialog.
 * 
 * @example
 * <ImageRotatorDialog imageUrl="https://example.com/image.jpg" onClose={handleClose} />
 * 
 * @returns {JSX.Element} The rendered ImageRotatorDialog component.
 */

import React, { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Slider,
    Box,
} from '@mui/material';

interface ImageRotatorDialogProps {
    imageUrl: string;
    onClose: () => void;
}

export default function ImageRotatorDialog({ imageUrl = '/placeholder.svg?height=300&width=300', onClose }: ImageRotatorDialogProps) {
    const [rotation, setRotation] = useState(0);

    /**
     * Updates the rotation state based on slider input.
     * @param {Event} event - The event that triggered the change.
     * @param {number | number[]} newValue - The new rotation value.
     */
    const handleRotationChange = (event: Event, newValue: number | number[]) => {
        setRotation(newValue as number);
    };

    /**
     * Resets the rotation angle to 0.
     */
    const resetRotation = () => setRotation(0);

    // Reset rotation to 0 whenever a new image URL is loaded.
    useEffect(() => {
        setRotation(0);
    }, [imageUrl]);

    return (
        <div>
            <Dialog open={!!imageUrl} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>Rotar imagen</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            mt: 2,
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                height: 500,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                overflow: 'hidden',
                            }}
                        >
                            <img
                                src={imageUrl}
                                alt="Rotatable image"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    transform: `rotate(${rotation}deg)`,
                                    transition: 'transform 0.1s ease-in-out',
                                }}
                            />
                        </Box>
                        <Box sx={{ width: '80%', mt: 2 }}>
                            <Slider
                                value={rotation}
                                onChange={handleRotationChange}
                                min={-180}
                                max={180}
                                step={null}
                                marks={[
                                    { value: -180, label: '-180°' },
                                    { value: -90, label: '-90°' },
                                    { value: 0, label: '0°' },
                                    { value: 90, label: '90°' },
                                    { value: 180, label: '180°' },
                                ]}
                                valueLabelDisplay="auto"
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={resetRotation}>Reiniciar</Button>
                    <Button onClick={onClose}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
